import React, {FormEvent} from 'react';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {GoChevronDown, GoChevronUp} from "react-icons/go";
import classNames from 'classnames';
import {useCategoriesWrapper, fetchCategories, CategoriesWrapper, WrappedCategory} from '../../../service/category';
import Api from '../../../service/api';
import Layout from '../../../components/layout/layout';
import HeadTitle from '../../../components/head_title';
import Breadcrumbs, {BreadcrumbPreviousPage} from '../../../components/breadcrumbs';
import TimeoutAlert from '../../../components/timeout_alert';
import Loading from '../../../components/loading';
import styles from '../../../styles/product_listing.module.scss';
import {extractAxiosError} from '../../../utils/error';
import {isUpperCase, toTitleCase} from '../../../utils/string';
import usePagination from '../../../hooks/pagination';
import ProductListingCard from '../../../components/product_listing_card';
import {useRegionLinkPrefix} from '../../../hooks/region';
import * as staticProps from '../../../lib/static_props';
import {wrapper} from "../../../store/store";
import basicColors from '../../../constants/basic_colors';
import {urlForReplacedCountryCode} from "../../../lib/region";
import querystring, {ParsedUrlQuery} from "querystring";
import {excludeProperties} from "../../../utils/object";
import categoryDescriptions from '../../../constants/localization/en/category_description';
import {getQueryFromQueries, useQuery} from "../../../hooks/use_query";
import {getQueriesFromQueries} from "../../../hooks/use_queries";
import {ProductListing} from "../../../types/models/product";

export interface SingleCategoryPageProps {
  categoryMeta?: string;
}

function SingleCategoryPage(props: SingleCategoryPageProps) {
  const router = useRouter();

  if (router.isFallback) {
    return <Loading/>;
  }

  const categories = useCategoriesWrapper();
  const regionLinkPrefix = useRegionLinkPrefix();

  const {query} = router;
  const categoryPath = useQuery('categoryPath');

  const [pageInt, paginator, setTotalPages] = usePagination();

  const [products, setProducts] = React.useState<ProductListing[] | null>(null);
  const [childCategories, setChildCategories] = React.useState<WrappedCategory[]>([]);
  const [minPrice, setMinPrice] = React.useState('');
  const [maxPrice, setMaxPrice] = React.useState('');
  const [selectedColors, setSelectedColors] = React.useState(new Set());
  const [currentCategory, setCurrentCategory] = React.useState<WrappedCategory | null>(null);
  const [previousPages, setPreviousPages] = React.useState<BreadcrumbPreviousPage[]>([]);
  const [isHidden, setIsHidden] = React.useState({
    color: true,
    price: true,
    categories: false
  });

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [textSearch, setTextSearch] = React.useState('');

  const populateSearchParams = React.useCallback(() => {
    const params = new URLSearchParams()

    if (maxPrice.length > 0) {
      params.set('maxPrice', maxPrice)
    }
    if (minPrice.length > 0) {
      params.set('minPrice', minPrice)
    }
    if (selectedColors.size > 0) {
      params.set('colors', Array.from(selectedColors).join(','))
    }
    params.set('page', pageInt.toString(10));

    return params
  }, [maxPrice, minPrice, selectedColors, pageInt])

  const fetchProducts = React.useCallback((category: WrappedCategory) => {
    if (!category) return

    const params = populateSearchParams()
    params.set('categories', category.id.toString())
    Api.product.search(params)
      .then((res) => {
        setProducts(res.data.products)
        setTotalPages(res.data.totalPages)
      })
      .catch(error => {
        setErrorMessage(extractAxiosError(error as Error))
      })

    let {parent} = category
    const newPreviousPages = []
    while (parent) {
      newPreviousPages.unshift({
        link: `/category/${parent.path}`,
        label: parent.name,
      })
      parent = parent.parent
    }
    setPreviousPages(newPreviousPages)
  }, [populateSearchParams])


  // Search function
  const handleSearchSubmit = React.useCallback((e: FormEvent) => {
    e.preventDefault()
    const params = populateSearchParams()
    params.set('text', textSearch)
    if (currentCategory) {
      params.set('categories', currentCategory.id.toString());
    }
    router.push(`${regionLinkPrefix}/product/search?${params.toString()}`)
  }, [populateSearchParams, textSearch])

  React.useEffect(() => {
    if (!router.isReady || !categories || !categoryPath) {
      return
    }

    const countryCode = getQueryFromQueries(query, 'countryCode');
    if (countryCode && !isUpperCase(countryCode)) {
      const url = urlForReplacedCountryCode(countryCode.toUpperCase(), router.pathname, querystring.stringify(excludeProperties(router.query, 'countryCode')))
      router.push(url)
      return
    }

    const newChildCategories = categories.getChildCategories(categoryPath);
    setChildCategories(newChildCategories);

    const category = categories.getCategoryByPath(categoryPath)
    setCurrentCategory(category)
    fetchProducts(category)

    {
      const [text, minPrice, maxPrice] = getQueriesFromQueries(query, 'text', 'minPrice', 'maxPrice');
      setTextSearch(text ?? '');
      setMinPrice(minPrice ?? '');
      setMaxPrice(maxPrice ?? '');
    }

    const colors = getQueryFromQueries(query, 'colors');
    if (!colors) {
      setSelectedColors(new Set());
    } else {
      setSelectedColors(new Set(colors.split(',')));
    }
  }, [categories, router.isReady, query])

  const handleSelectColor = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedColors = new Set(selectedColors)
    if (e.target.checked) {
      newSelectedColors.add(e.target.value)
    } else {
      newSelectedColors.delete(e.target.value)
    }
    setSelectedColors(newSelectedColors)
  }, [selectedColors]);

  const handleFilter = () => {
    router.push(`${regionLinkPrefix}/category/${categoryPath}?${populateSearchParams().toString()}`)
  }

  const toggleSection = React.useCallback((key: keyof typeof isHidden) => () => {
    const newIsHidden = Object.assign({}, isHidden, {
      [key]: !isHidden[key]
    });
    setIsHidden(newIsHidden);
  }, [isHidden]);

  let content
  if (products !== null) {
    content = <React.Fragment>
      <div className="row">
        <div className="col-md-4 col-lg-3 mb-4">
          <div className={`card ${styles.filters}`}>
            <div className="card-body">
              <h4 className={styles.filters_header} onClick={toggleSection('categories')}>
                Categories

                {isHidden['categories'] ? (
                  <GoChevronDown className="float-right text-primary"/>
                ) : (
                  <GoChevronUp className="float-right text-primary"/>
                )}
              </h4>

              <div className={classNames({
                'd-none': isHidden['categories']
              })}>
                {currentCategory && (
                  <div className={styles.filters_parentCategory}>
                    {currentCategory.name}
                  </div>
                )}
                {childCategories.map((c, index) => (
                  <React.Fragment key={index}>
                    <Link
                      href={`${regionLinkPrefix}/category/${c.path}`}
                      className={styles.filters_category}>
                      {c.name}
                    </Link>
                  </React.Fragment>
                ))}
              </div>
              <hr/>

              <h4 className={styles.filters_header} onClick={toggleSection('price')}>
                Price
                {isHidden['price'] ? (
                  <GoChevronDown className="float-right text-primary"/>
                ) : (
                  <GoChevronUp className="float-right text-primary"/>
                )}
              </h4>
              <div className={classNames({
                'd-none': isHidden['price']
              })}>
                <div className="form-group">
                  <label className="form-label">Min Price</label>
                  <input type="number" className="form-control" onChange={e => setMinPrice(e.target.value)}
                         value={minPrice}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Max Price</label>
                  <input type="number" className="form-control" onChange={e => setMaxPrice(e.target.value)}
                         value={maxPrice}/>
                </div>
              </div>
              <hr/>

              <h4 className={styles.filters_header} onClick={toggleSection('color')}>
                Color
                {isHidden['color'] ? (
                  <GoChevronDown className="float-right text-primary"/>
                ) : (
                  <GoChevronUp className="float-right text-primary"/>
                )}
              </h4>
              <div className={classNames({
                'd-none': isHidden['color']
              })}>
                <div className="form-group">
                  {basicColors.map(({name, hex}) => (
                    <div className="form-check" key={name}>
                      <input
                        id={`color-filter-${name}`}
                        className="form-check-input"
                        type="checkbox"
                        value={name}
                        checked={selectedColors.has(name)}
                        onChange={handleSelectColor}/>
                      <label className="form-check-label" htmlFor={`color-filter-${name}`}>
                        <div className={styles.filterColorPreview} style={{backgroundColor: hex}}/>
                        {toTitleCase(name)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <hr/>

              <button type="button" className="btn btn-primary" onClick={handleFilter}>Filter</button>
            </div>
          </div>
        </div>
        <div className="col-md-8 col-lg-9">
          <form onSubmit={handleSearchSubmit}>
            <div className="row mb-3">
              <div className="col-md">
                <input
                  type="text"
                  className="form-control"
                  value={textSearch}
                  onChange={(e) => setTextSearch(e.target.value)}
                  placeholder={`Search in ${currentCategory ? currentCategory.name : 'category'}`}
                />
              </div>
              <div className="col-md-auto">
                <button type="submit" className="btn btn-primary">Search</button>
              </div>
            </div>
          </form>
          <div className="row">
            {products.map((product, index) => (
              <div className="col-6 col-lg-3 mb-4" key={index}>
                <ProductListingCard
                  product={product}
                />
              </div>
            ))}
          </div>

          {paginator}
        </div>
      </div>
    </React.Fragment>
  } else {
    content = <Loading/>
  }

  return <Layout>
    <HeadTitle title={currentCategory ? currentCategory.name : 'Category'}>
      {props.categoryMeta &&
        <meta name="description" content={props.categoryMeta}/>
      }
    </HeadTitle>
    <Breadcrumbs
      previousPages={previousPages}
      currentPage={currentCategory ? currentCategory.name : 'Current Category'}
    />
    <TimeoutAlert
      message={errorMessage}
      onHide={() => setErrorMessage(null)}
    />

    {content}
  </Layout>
}

export default SingleCategoryPage;

export async function getStaticPathsWithoutCountry() {
  const categories = await fetchCategories();
  const categoriesWrapper = new CategoriesWrapper(categories);
  const categoryPaths = Object.keys(categoriesWrapper.categoryPathMap);

  const paths = categoryPaths.map(categoryPath => ({
    params: {
      categoryPath: encodeURIComponent(categoryPath)
    }
  }))

  return {
    paths,
    fallback: false
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true
  };
}

interface SingleCategoryPageParams extends ParsedUrlQuery {
  categoryPath: string;
}

export const getStaticProps = wrapper.getStaticProps<SingleCategoryPageProps>(store => async (context) => {
  const params = context.params as SingleCategoryPageParams;
  await staticProps.populateServerSideStore(store.dispatch);
  const props: SingleCategoryPageProps = {};
  const categoryPath = getQueryFromQueries(params, 'categoryPath')!;

  const categoryDescription = categoryDescriptions[categoryPath];
  if (categoryDescription) {
    props.categoryMeta = categoryDescription;
  }

  return {
    props
  };
})
